import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { api } from '../api/client';
import { toast } from '../stores/toasts';
import { StatusBadge } from '../components/StatusBadge';
import { ReviewModal } from '../components/ReviewModal';
import { formatUzs, timeAgo } from '../lib/format';
import { useState } from 'react';

export function MyOrdersPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [reviewOrderId, setReviewOrderId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-orders'],
    queryFn: () => api.myOrders(),
  });

  const orders = data ?? [];
  const active = orders.filter((o: any) => !['DELIVERED', 'CANCELLED'].includes(o.status));
  const past = orders.filter((o: any) => ['DELIVERED', 'CANCELLED'].includes(o.status));

  return (
    <>
      <h3>📋 Mening buyurtmalarim</h3>
      {isLoading ? (
        <div className="spinner" />
      ) : orders.length === 0 ? (
        <div className="empty">
          <div className="emoji">📭</div>
          <div>Buyurtmalar yo'q</div>
          <div className="muted" style={{ marginTop: 4 }}>Servisga borganingizda avtomatik paydo bo'ladi</div>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <>
              <div className="field-label" style={{ marginBottom: 6 }}>Faol</div>
              {active.map((o: any) => (
                <div key={o.id} className="card" onClick={() => nav(`/my-orders/${o.id}`)} style={{ cursor: 'pointer' }}>
                  <div className="row-between" style={{ marginBottom: 4 }}>
                    <div style={{ fontWeight: 600 }}>{o.workshop.name}</div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
                    🚗 {o.car.plate} · {o.car.brand} {o.car.model}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {o.problem.length > 60 ? o.problem.slice(0, 60) + '…' : o.problem}
                  </div>
                  <div className="row-between" style={{ marginTop: 6 }}>
                    <span className="muted" style={{ fontSize: 11 }}>{timeAgo(o.receivedAt)}</span>
                    <span className="amount" style={{ fontWeight: 600 }}>{formatUzs(o.totalCached)}</span>
                  </div>
                </div>
              ))}
            </>
          )}

          {past.length > 0 && (
            <>
              <div className="field-label" style={{ marginTop: 14, marginBottom: 6 }}>Tarix</div>
              {past.map((o: any) => (
                <div key={o.id} className="card">
                  <div className="row-between" style={{ marginBottom: 4 }}>
                    <div style={{ fontWeight: 600 }}>{o.workshop.name}</div>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
                    🚗 {o.car.plate} · {o.car.brand} {o.car.model}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {o.deliveredAt
                      ? `Topshirildi: ${format(parseISO(o.deliveredAt), 'dd MMM yyyy')}`
                      : `Qabul: ${format(parseISO(o.receivedAt), 'dd MMM yyyy')}`}
                  </div>
                  <div className="row-between" style={{ marginTop: 6 }}>
                    <span className="amount" style={{ fontWeight: 600 }}>{formatUzs(o.totalCached)}</span>
                    {o.status === 'DELIVERED' && !o.review && (
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={(e) => { e.stopPropagation(); setReviewOrderId(o.id); }}
                      >
                        ⭐ Baho berish
                      </button>
                    )}
                    {o.review && (
                      <span className="tag success">⭐ {o.review.rating}</span>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      {reviewOrderId && (
        <ReviewModal
          orderId={reviewOrderId}
          onClose={() => setReviewOrderId(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ['my-orders'] })}
        />
      )}
    </>
  );
}
